import React, { useState, useMemo, createContext, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box } from '@mui/material';
import { getTheme } from './theme';
import ProtectedRoute from './auth/ProtectedRoute';
import { ActiveTeamProvider } from './context/ActiveTeamContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import pushManager from './utils/pushManager';

import Login from './pages/basics/Login';
import Home from './pages/basics/Home';
import ResetPassword from './pages/basics/ResetPassword';
import VerifyEmail from './pages/basics/VerifyEmail';
import Termine from './pages/termine/Termine';
import TeamManagement from './pages/teams/TeamManagement';
import CreateTeam from './pages/teams/CreateTeam';
import JoinTeam from './pages/teams/JoinTeam';
import TeamInvitations from './pages/teams/TeamInvitations';
import ApproveTeamWithCode from './pages/ApproveTeamWithCode';
import ApproveTrainerWithCode from './pages/ApproveTrainerWithCode';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminApprovals from './pages/admin/AdminApprovals';
import GlobalSearch from './pages/search/GlobalSearch';
import Absences from './pages/user/Absences';
import TeamFeed from './pages/teams/TeamFeed';
import Treasury from './pages/teams/Treasury';
import Notifications from './pages/user/Notifications';
import LineupBuilder from './pages/teams/LineupBuilder';
import PlayerStats from './pages/teams/PlayerStats';
import TeamOverview from './pages/teams/TeamOverview';
import TeamGallery from './pages/teams/TeamGallery';
import TerminDetails from './pages/termine/TerminDetails';
import Header from './components/navigation/Header';
import BottomNavBar from './components/navigation/BottomNavBar';
import PublicProfile from './pages/user/PublicProfil';
import tokenManager from './utils/tokenManager';
import Settings from './pages/user/Settings';

export const ThemeConfigContext = createContext();

function AppContent() {
  const { isAuthenticated } = useAuth();
  const [mode, setMode] = useState(localStorage.getItem('themeMode') || 'light');
  const [primary, setPrimary] = useState(localStorage.getItem('themePrimary') || '');
  const [secondary, setSecondary] = useState(localStorage.getItem('themeSecondary') || '');

  const themeConfig = useMemo(() => ({
    mode,
    setMode: (m) => { localStorage.setItem('themeMode', m); setMode(m); },
    primary,
    setPrimary: (c) => { if (c) localStorage.setItem('themePrimary', c); else localStorage.removeItem('themePrimary'); setPrimary(c); },
    secondary,
    setSecondary: (c) => { if (c) localStorage.setItem('themeSecondary', c); else localStorage.removeItem('themeSecondary'); setSecondary(c); }
  }), [mode, primary, secondary]);

  const theme = useMemo(() => getTheme(mode, primary, secondary), [mode, primary, secondary]);

  // 403 global abfangen
  useEffect(() => {
    const handleForbidden = () => {
      tokenManager.clearTokens();        // User ausloggen
      localStorage.removeItem('activeTeamId'); // User-Daten entfernen
      window.location.href = '/';    // Zur Login-Seite
    };

    window.addEventListener('forbidden', handleForbidden);
    return () => window.removeEventListener('forbidden', handleForbidden);
  }, []);

  // Push-Manager initialisieren wenn User eingeloggt ist
  useEffect(() => {
    if (isAuthenticated) {
      const initializePush = async () => {
        try {
          const success = await pushManager.setupPushNotifications();
          if (success) {
            console.log('Push notifications initialized successfully');
          } else {
            console.log('Push notifications initialization failed or not supported');
          }
        } catch (error) {
          console.error('Error initializing push notifications:', error);
        }
      };

      initializePush();
    }
  }, [isAuthenticated]);

  return (
    <ThemeConfigContext.Provider value={themeConfig}>
      <ActiveTeamProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <BrowserRouter>
            <Routes>
              <Route path="/auth" element={<Login />} />
              <Route path="/join/:token" element={<ProtectedRoute><JoinTeam /></ProtectedRoute>} />
              <Route path="/approve-team/:teamId" element={<ApproveTeamWithCode />} />
              <Route path="/approve-trainer/:userId" element={<ApproveTrainerWithCode />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />

              <Route element={<ProtectedRoute />}>
                <Route path="*" element={
                  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', boxSizing: 'border-box' }}>
                    <Header />
                    <Box component="main" sx={{ flexGrow: 1, pb: '90px' }}>
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/termine" element={<Termine />} />
                        <Route path="/termine/:id" element={<TerminDetails />} />
                        <Route path="/teams" element={<TeamManagement />} />
                        <Route path="/teams/invitations" element={<TeamInvitations />} />
                        <Route path="/teams/create" element={<CreateTeam />} />
                        <Route path="/admin" element={<AdminDashboard />} />
                        <Route path="/admin/approvals" element={<AdminApprovals />} />
                        <Route path="/search" element={<GlobalSearch />} />
                        <Route path="/absences" element={<Absences />} />
                        <Route path="/teams/:teamId/feed" element={<TeamFeed />} />
                        <Route path="/teams/:teamId/overview" element={<TeamOverview />} />
                        <Route path="/teams/:teamId/gallery" element={<TeamGallery />} />
                        <Route path="/teams/:teamId/treasury" element={<Treasury />} />
                        <Route path="/teams/:teamId/lineup/:terminId?" element={<LineupBuilder />} />
                        <Route path="/teams/:teamId/stats" element={<PlayerStats />} />
                        <Route path="/notifications" element={<Notifications />} />
                        <Route path="/users/:userId" element={<PublicProfile />} />
                        <Route path="/settings" element={<Settings />} />
                      </Routes>
                    </Box>
                    <BottomNavBar />
                  </Box>
                } />
              </Route>
            </Routes>
          </BrowserRouter>
        </ThemeProvider>
      </ActiveTeamProvider>
    </ThemeConfigContext.Provider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
